import React, {useEffect, useState} from "react";
import {Box, Button, Heading, Stack, TextBlock} from "@unibuddy/patron";
import {useMutation, useQuery} from "@apollo/client";
import {useHistory, useLocation, useParams} from "react-router-dom";
import ThemedLink from "shared/Navigation/components/ThemedLink/ThemedLink";
import {useTranslation} from "@unibuddy/intl";
import {rootURL} from "shared/Utils/WhereAmI";
import useAnalytics from "shared/AnalyticsNew/hooks/useAnalytics";
import {sharedEvents} from "shared/AnalyticsNew/constants/productSpecification";

import {blogPostDetailQuery} from "../data/blogPostDetailQuery";
import {ContentDetailsAvatar, ContentDetailsHeader} from "../components/ContentDetailsHeader";
import {BlogDetailHeader} from "../components/BlogDetailHeader";
import {PageTitle} from "../../../../components/PageTitle/PageTitle";
import {BlogPostText} from "../components/BlogPostText";
import {BlogChatToAmbassadorButton} from "../components/BlogChatNowButton";
import {BlogDetailMedia} from "../components/media/BlogDetailMedia";
import {useSearchQuery} from "../../../../hooks/useSearchQuery";
import {
    BlogPostDetailQueryQuery,
    BlogPostDetailQueryQueryVariables,
} from "../../../../types/generated";
import {Post} from "../../../../widgets/modules/Content/types";
import {getContentType} from "../utils";
import {viewBlogMutation} from "../data/viewBlogMutation";
import {IntlNamespaces} from "../../../../translations/types";
import {INTL_CHAT_WITH_STUDENTS, INTL_FIND_MORE} from "../../../../translations/blogs";

type MatchParams = {
    universitySlug: string;
    id: string;
};

const DEFAULT_PROFILE_IMAGE =
    "https://s3-eu-west-1.amazonaws.com/unibudy-prod/StaticAssets/default_profile.png";

export const BlogDetailsPage = () => {
    const {t} = useTranslation([IntlNamespaces.COMMON, IntlNamespaces.BLOGS]);
    const params = useParams<MatchParams>();
    const search = useSearchQuery();
    const history = useHistory();
    const location = useLocation();
    const {trackEvent} = useAnalytics();
    const [viewBlog] = useMutation(viewBlogMutation);
    const [shouldShowAuthorChatButton, setShouldShowAuthorChatButton] = useState(false);

    const {data, loading, error} = useQuery<
        BlogPostDetailQueryQuery,
        BlogPostDetailQueryQueryVariables
    >(blogPostDetailQuery, {
        variables: {
            postId: params.id,
        },
    });

    const baseUrl = rootURL(location.pathname);
    const post = data?.post as Post | null;
    const isAuthorArchived = post?.author?.archived;
    const isAuthorActive = post?.author?.active;
    const isVideo = post?.youtubeVideo !== null || post?.videoContent != null;
    const autoPlay = search.get("autoPlay") === "1";

    const mentorId = post?.author?.id ?? "Unknown ID";
    const buddyType = post?.author?.accountRole === "staff" ? "staff" : "student";
    const authorProfileUrl = `${baseUrl}/buddies/${buddyType}/${mentorId}?${search.toString()}`;

    const onShare = () => {
        trackEvent(sharedEvents.blogShared.name, {
            id: data?.post?.id,
            title: data?.post?.title ?? "Unknown Title",
            topic: data?.post?.topic?.name ?? "Unknown Topic",
            authorId: mentorId,
        });
    };

    const onViewProfile = () => {
        trackEvent(sharedEvents.buddyViewed.name, {
            id: mentorId,
            buddyType: data?.post?.author?.accountRole ?? "Unknown Buddy Type",
        });
    };

    const onChatNow = () => {
        trackEvent(sharedEvents.chatNowClickedFromContent.name, {
            mentorId,
            buddyType: post?.author?.accountRole ?? "Unknown Buddy Type",
        });
        history.push(`${baseUrl}/inbox/chatwith/${mentorId}?${search.toString()}`);
    };

    const onChatWithStudents = () => {
        trackEvent(sharedEvents.chatWithStudentsClickedFromContent.name, {
            page: "Blog Details Page",
            cta: "Chat with students",
        });
        history.push(`${baseUrl}/buddies/students?${search.toString()}`);
    };

    useEffect(() => {
        setShouldShowAuthorChatButton(isAuthorActive && !isAuthorArchived);
        if (!loading && post) {
            const contentType = getContentType(post);

            viewBlog({
                variables: {
                    blogId: post.id,
                    userId: post.author?.id,
                },
            });

            trackEvent(sharedEvents.blogViewed.name, {
                id: post.id,
                contentType,
            });
        }
    }, [isAuthorActive, isAuthorArchived, loading, post, trackEvent, viewBlog]);

    // TODO: NEED TO CREATE A LOADING STATE
    if (loading) {
        return <div>Loading...</div>;
    }

    // TODO: NEED TO CREATE A ERROR STATE
    if (error) {
        console.error("Error loading blog post details:", error);
        return <div>Error: Something went wrong. Please try again later.</div>;
    }

    return (
        <Box display={"flex"} flexDirection={"column"}>
            <PageTitle title={post?.title} />
            <Box position={"relative"}>
                <Box
                    id={"coverImage"}
                    width="100%"
                    maxH={isVideo ? "100%" : "286px"}
                    overflow="hidden"
                >
                    <BlogDetailMedia post={post} autoPlay={autoPlay} />
                </Box>
            </Box>
            <Box
                backgroundColor="white"
                shadow="medium"
                width="100%"
                maxW="500px"
                borderRadius="xxsmall"
                padding="small-medium"
                zIndex={2}
                marginTop={!isVideo ? [-160, -100] : [0]}
                marginX={"auto"}
            >
                <Box display={"flex"} justifyContent={"center"} flexDirection={"column"}>
                    <Stack space={"small-medium"}>
                        <ThemedLink
                            to={authorProfileUrl}
                            external={false}
                            onClick={onViewProfile}
                            style={{textDecoration: "none"}}
                        >
                            <ContentDetailsHeader
                                authorDegree={post?.author?.degree?.name ?? ""}
                                authorName={post?.author?.firstName ?? ""}
                                avatar={
                                    <ContentDetailsAvatar
                                        url={post?.author?.profilePhoto ?? DEFAULT_PROFILE_IMAGE}
                                        alt={`${post?.author?.firstName}'s Avatar`}
                                        primaryFlag={post?.author?.country ?? null}
                                        secondaryFlag={
                                            post?.author?.mentorFields?.secondaryFlag ?? null
                                        }
                                    />
                                }
                            />
                        </ThemedLink>
                        <Box paddingTop={"small"}>
                            <Heading level={1} size={"large"} weight={700}>
                                <span>{post?.title}</span>
                            </Heading>
                        </Box>
                        <BlogDetailHeader
                            modified={post?.modified ?? ""}
                            numberOfViews={post?.numberOfViews?.toString() ?? ""}
                            readingTime={post?.readingTime?.toString() ?? ""}
                            space={"medium"}
                            shareUrl={post?.shareUrl ?? ""}
                            onShare={onShare}
                        />
                    </Stack>

                    <Box
                        borderBottomWidth={1}
                        width="100%"
                        borderColor="grey200"
                        marginY={"small"}
                    />

                    <BlogPostText body={post?.body ?? ""} />

                    <Stack space={"small-medium"}>
                        <hr />
                        {shouldShowAuthorChatButton ? (
                            <BlogChatToAmbassadorButton
                                name={post?.author?.firstName ?? "our ambassador"}
                                onChatNow={onChatNow}
                            />
                        ) : (
                            <Box
                                borderRadius={"xxsmall"}
                                borderColor={"var(--theme-primary)"}
                                borderWidth={1}
                                padding="medium"
                                width="100%"
                                minW={200}
                            >
                                <Stack space={"medium"}>
                                    <TextBlock weight={"600"} size={"medium"} align={"center"}>
                                        {t(
                                            `${INTL_FIND_MORE.namespace}:${INTL_FIND_MORE.key}`,
                                            INTL_FIND_MORE.defaultValue,
                                        )}
                                    </TextBlock>
                                    <Button color="university" block onClick={onChatWithStudents}>
                                        {t(
                                            `${INTL_CHAT_WITH_STUDENTS.namespace}:${INTL_CHAT_WITH_STUDENTS.key}`,
                                            INTL_CHAT_WITH_STUDENTS.defaultValue,
                                        )}
                                    </Button>
                                </Stack>
                            </Box>
                        )}
                    </Stack>
                </Box>
            </Box>
        </Box>
    );
};
